import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Paper,
  TableContainer,
  Avatar,
  TextField,
  Checkbox,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import MDButton from "../../../../components/MDButton";
import EditIcon from "@mui/icons-material/Edit";
import { useSubmissionStore } from "../../../../models/applications/SubmissionStore";
import { get, post, request } from "../../../../requests";
import MDBox from "components/MDBox";
import DOMPurify from "dompurify";
import NotesEditor from "pages/applications/components/NotesEditor";

interface NotesContentProps {
  appId: number;
  isReadOnly: boolean;
}

interface Note {
  id: number;
  createdBy: string;
  date: string;
  note: string;
  appDealNo: string;
  notesThrough: string;
}

const getRandomColor = (name: string): string => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  const hue = Math.abs(hash % 360);
  return `hsl(${hue}, 80%, 40%)`;
};

const getInitialsFromCreatedBy = (createdBy: string) => {
  if (!createdBy) return "";
  const words = createdBy.split(" ");
  const initials = words.map((word) => word[0]?.toUpperCase()).join("");

  return initials;
};

const NotesContent: React.FC<NotesContentProps> = ({ appId, isReadOnly }) => {
  const { merchantBusinessInfo } = useSubmissionStore();
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [notes, setNotes] = useState<any[]>([]);
  const [userColors, setUserColors] = useState<Map<string, string>>(new Map());
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editedNoteText, setEditedNoteText] = useState("");
  const [showMyNotes, setShowMyNotes] = useState(false);
  const [notesThrough, setNotesThrough] = useState<string>("applicationNotes");
  const [filters, setFilters] = useState<{ [key: string]: string }>({
    date: "",
    createdBy: "",
    appDealNo: "",
    notes: "",
  });

  useEffect(() => {
    if (merchantBusinessInfo?.merchantId) {
      fetchAllNotes();
    }
  }, [merchantBusinessInfo]);

  const fetchAllNotes = () => {
    get(`fetchAllAvailableMerchantNotes/${merchantBusinessInfo.merchantId}`)
      .then((response: Note[]) => {
        console.log("Fetched notes:", response);
        setNotes(response);

        const newUserColors = new Map<string, string>();
        response.forEach((note: Note) => {
          if (!newUserColors.has(note.createdBy)) {
            newUserColors.set(note.createdBy, getRandomColor(note?.createdBy?.split(" ")[0] || ""));
          }
        });
        setUserColors(newUserColors);
      })
      .catch((error) => {
        console.error("Error fetching all notes:", error);
      });
  };

  const handleAddNote = () => setIsAddingNote(true);

  const handleCancel = () => {
    setIsAddingNote(false);
    setNoteText("");
  };

  const handleEditNote = (noteId: number, currentNoteText: string, notesThrough: string) => {
    setEditingNoteId(noteId);
    setEditedNoteText(currentNoteText);
    setNotesThrough(notesThrough);
  };

  const handleSave = async () => {
    const logInUserId = localStorage.getItem("logInUserId");
    if (!logInUserId) {
      console.error("User ID not found in localStorage");
      return;
    }
    const payload = { createdBy: logInUserId, dealApplicationId: appId, note: noteText };
    try {
      await post("/applicationNotes/create", payload);
      console.log("Note saved successfully!");
      setIsAddingNote(false);
      setNoteText("");
      setNotesThrough("applicationNotes");
      fetchAllNotes();
    } catch (error) {
      console.error("Error saving note:", error);
    }
  };

  const handleSaveEditedNote = async () => {
    if (!editingNoteId) return;
    try {
      const payload =
        notesThrough === "dealNotes" || notesThrough === "partyNotes"
          ? { notes: editedNoteText }
          : { note: editedNoteText };
      await request("put", `/${notesThrough}/${editingNoteId}`, payload, { baseURL: "" });
      console.log("Note updated successfully!");
      setEditingNoteId(null);
      setEditedNoteText("");
      setNotesThrough("applicationNotes");
      fetchAllNotes();
    } catch (error) {
      console.error("Error updating note:", error);
    }
  };

  const handleDeleteNote = async (noteId: number, notesThrough: string) => {
    try {
      const endpoint = `/${notesThrough}/${noteId}`;
      await request("delete", endpoint, {}, { baseURL: "" });
      console.log(`Note with ID ${noteId} deleted successfully!`);
      fetchAllNotes();
    } catch (error) {
      console.error("Error deleting note:", error);
    }
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowMyNotes(event.target.checked);
  };

  const loggedInUserId = localStorage.getItem("logInUserId");

  const filteredNotes = notes.filter((note) => {
    const matchesFilters =
      (filters.date ? new Date(note.date).toLocaleDateString().includes(filters.date) : true) &&
      (filters.createdBy
        ? note.createdBy.toLowerCase().includes(filters.createdBy.toLowerCase())
        : true) &&
      (filters.appDealNo
        ? note.appDealNo.toLowerCase().includes(filters.appDealNo.toLowerCase())
        : true) &&
      (filters.notes ? note.note.toLowerCase().includes(filters.notes.toLowerCase()) : true);

    if (showMyNotes) {
      return matchesFilters && note.createdBy.toLowerCase() === loggedInUserId.toLowerCase();
    }
    return matchesFilters;
  });

  return (
    <Box
      sx={{
        p: 2,
        border: "1px solid rgba(0, 0, 0, 0.1)",
        borderRadius: "10px",
        backgroundColor: "#ffffff",
      }}
    >
      <div style={isReadOnly ? { pointerEvents: "none", opacity: 0.7 } : {}}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" marginLeft="14px">
            <Checkbox checked={showMyNotes} onChange={handleCheckboxChange} />
            <Typography variant="body1" sx={{ ml: 1, fontWeight: "bold", fontSize: "18px" }}>
              My notes
            </Typography>
          </Box>
          <MDButton variant="contained" color="primary" onClick={handleAddNote}>
            Add A Note
          </MDButton>
        </Box>

        {isAddingNote && (
          <Box sx={{ mt: 2, p: 2, bgcolor: "#f9f9f9", borderRadius: 1 }}>
            <Typography variant="subtitle1" gutterBottom>
              New Note
            </Typography>

            <NotesEditor
              initialHtml={noteText}
              updateEmailContent={setNoteText}
              templateName="edit-note"
            />

            <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
              <MDButton
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={!noteText.trim()}
              >
                Save
              </MDButton>
              <MDButton variant="contained" color="error" onClick={handleCancel}>
                Cancel
              </MDButton>
            </Box>
          </Box>
        )}

        <Box sx={{ mt: 2, p: 1, borderRadius: "10px", marginTop: "5px" }}>
          {filteredNotes.length > 0
            ? filteredNotes.map((note) => {
                const color = userColors.get(`${note.firstName}-${note.lastName}`) || "#000";
                const isEditing = editingNoteId === note.id && notesThrough === note?.notesThrough;

                return (
                  <Box
                    key={note.id}
                    sx={{
                      mb: 1,
                      p: 1,
                      borderBottom: "1px solid #ddd",
                      "&:hover": {
                        backgroundColor: "#f9f3f3",
                        boxShadow: "none",
                      },
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar
                        sx={{
                          width: 35,
                          height: 35,
                          backgroundColor: userColors.get(note.createdBy),
                          fontSize: 14,
                        }}
                      >
                        {getInitialsFromCreatedBy(note.createdBy)}
                      </Avatar>
                      <Typography
                        variant="body1"
                        fontSize="16px"
                        fontWeight="bold"
                        marginLeft="5px"
                      >
                        {note.createdBy}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontSize="13.5px"
                        marginLeft="8px"
                        color="textSecondary"
                      >
                        {new Date(note.date).toLocaleDateString()}
                      </Typography>

                      <Box sx={{ gap: "8px" }}>
                        <IconButton
                          color="primary"
                          sx={{ fontSize: "1.2rem" }}
                          onClick={() => handleEditNote(note.id, note.note, note?.notesThrough)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          sx={{ fontSize: "1.2rem" }}
                          onClick={() => handleDeleteNote(note.id, note.notesThrough)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>

                    {isEditing ? (
                      <Box sx={{ mt: 1 }}>
                        <NotesEditor
                          initialHtml={editedNoteText}
                          updateEmailContent={setEditedNoteText}
                          templateName="edit-note"
                        />

                        <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
                          <MDButton
                            variant="contained"
                            color="primary"
                            onClick={handleSaveEditedNote}
                          >
                            Save
                          </MDButton>

                          <MDButton
                            variant="contained"
                            color="error"
                            onClick={() => setEditingNoteId(null)}
                          >
                            Cancel
                          </MDButton>
                        </Box>
                      </Box>
                    ) : (
                      <MDBox
                        sx={{
                          mt: 1,
                          overflowY: "auto",
                          fontSize: "14px",
                          color: "#000",
                          marginLeft: "5px",
                        }}
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(note?.note) }}
                      />
                    )}
                  </Box>
                );
              })
            : null}
        </Box>
      </div>
    </Box>
  );
};

export default NotesContent;
